import { useAuth } from 'react-oidc-context';
import { ExternalLink, ShieldCheck } from 'lucide-react';
import { ROLE_LABELS } from '@electrodavisan/shared';
import { config } from '../../app/config';
import { initialsOf, useCurrentUser } from '../../auth/useSession';
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  ErrorState,
  PageHeader,
  SkeletonList,
} from '../../components/ui';

/**
 * Perfil del usuario.
 *
 * Todos los datos vienen de Keycloak. El Ecosystem no guarda una segunda
 * contrasena ni una copia editable del perfil: cambiar nombre, email o
 * contrasena se hace en la consola de cuenta de Keycloak.
 */
export function ProfilePage(): JSX.Element {
  const auth = useAuth();
  const { data: user, isLoading, isError, refetch } = useCurrentUser();

  const accountUrl = `${config.keycloakUrl}/realms/${config.keycloakRealm}/account`;

  if (isLoading) {
    return (
      <>
        <PageHeader title="Mi perfil" />
        <SkeletonList rows={3} />
      </>
    );
  }

  if (isError || !user) {
    return (
      <>
        <PageHeader title="Mi perfil" />
        <ErrorState onRetry={() => void refetch()} />
      </>
    );
  }

  return (
    <>
      <PageHeader title="Mi perfil" description="Tu identidad dentro del ecosistema." />

      <div className="grid gap-6 lg:grid-cols-2">
        <Card>
          <CardBody className="flex items-center gap-4">
            <span
              aria-hidden
              className="flex h-16 w-16 shrink-0 items-center justify-center rounded-2xl bg-primary-soft text-lg font-semibold text-primary-bright"
            >
              {initialsOf(user)}
            </span>
            <div className="min-w-0">
              <h2 className="truncate text-lg font-semibold text-text-primary">
                {user.fullName}
              </h2>
              <p className="truncate text-sm text-text-muted">{user.email ?? 'Sin email'}</p>
              <div className="mt-2 flex flex-wrap gap-1.5">
                {user.roles.map((role) => (
                  <Badge key={role} tone="primary">
                    {ROLE_LABELS[role]}
                  </Badge>
                ))}
              </div>
            </div>
          </CardBody>
        </Card>

        <Card>
          <CardHeader title="Datos de la cuenta" />
          <CardBody className="space-y-3 text-sm">
            <Row label="Nombre" value={user.firstName ?? '-'} />
            <Row label="Apellido" value={user.lastName ?? '-'} />
            <Row label="Usuario" value={user.username ?? '-'} />
            <Row label="Email" value={user.email ?? '-'} />
            <Row label="Identificador (sub)" value={user.sub} mono />
          </CardBody>
        </Card>

        <Card className="lg:col-span-2">
          <CardHeader
            title="Seguridad e identidad"
            description="Gestionada integramente por Keycloak."
          />
          <CardBody className="space-y-4">
            <div className="flex items-start gap-3 rounded-lg bg-surface-secondary p-4">
              <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-success" aria-hidden />
              <div className="min-w-0 text-sm text-text-secondary">
                <p className="font-medium text-text-primary">
                  Tu contrasena no se almacena en {config.appName}
                </p>
                <p className="mt-1 leading-relaxed">
                  La identidad pertenece al proveedor de identidad del ecosistema. Desde alli
                  puedes cambiar tu contrasena, revisar tus sesiones activas y configurar la
                  verificacion en dos pasos.
                </p>
              </div>
            </div>

            <div className="flex flex-wrap gap-2">
              <a href={accountUrl} target="_blank" rel="noopener noreferrer">
                <Button
                  variant="secondary"
                  rightIcon={<ExternalLink className="h-3.5 w-3.5" aria-hidden />}
                >
                  Administrar mi cuenta
                </Button>
              </a>
              <Button
                variant="ghost"
                onClick={() => {
                  void auth.signoutRedirect({
                    post_logout_redirect_uri: `${window.location.origin}/`,
                  });
                }}
              >
                Cerrar sesion
              </Button>
            </div>
          </CardBody>
        </Card>
      </div>
    </>
  );
}

function Row({
  label,
  value,
  mono,
}: {
  label: string;
  value: string;
  mono?: boolean;
}): JSX.Element {
  return (
    <div className="flex flex-col gap-0.5 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
      <span className="text-[13px] text-text-muted">{label}</span>
      <span
        className={
          mono
            ? 'break-all font-mono text-[12px] text-text-primary'
            : 'break-words text-text-primary'
        }
      >
        {value}
      </span>
    </div>
  );
}
